
import { ReadingStats, SessionResult } from '../types';
import { initDB, upsertDailyStat, getDailyStat, getAllDailyStats } from './indexedDB';

// --- Pre-calculated Guest Mode Constants ---
// Stream 1: 200 -> 500 WPM over 30s. Avg = 350.
// Stream 2: 300 -> 750 WPM over 30s. Avg = 525.
export const GUEST_TRIAL_1_CONFIG = { start: 200, end: 500, duration: 30 };
export const GUEST_TRIAL_2_CONFIG = { start: 300, end: 750, duration: 30 };

export const TRIAL_STATS = {
  baselineAvg: 350,
  pushAvg: 525,
  improvementPct: 50, // (525 - 350) / 350 * 100
  baselineMax: 500,
  pushMax: 750,
  baselineWords: 175, // 350 wpm * 0.5 min
  pushWords: 263    // 525 wpm * 0.5 min = 262.5, rounded to 263
};

// Upsell page trial stats defaults (used when no localStorage data)
export const TRIAL_STATS_DEFAULTS = {
  sessionsCompleted: 3,
  wordsRead: 0,
  maxWpm: 0,
  avgWpm: 0,
  timeSaved: 0,
  sessions: 0,
  booksCompleted: 0,
  genresRead: [],
  timeSpentSeconds: 0,
};

export function formatTimeSaved(seconds: number): string {
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours} hours ${remainingMinutes} minutes`;
}

export function calculateImprovement(avgWpm: number): number {
  if (avgWpm <= 200) return 0;
  return Math.round(((avgWpm - 200) / 200) * 100);
}

export const calculateEstimatedTimeSeconds = (wordCount: number, wpm: number): number => {
  if (wpm < 0) return -2; // Return -2 for negative WPM as expected by tests
  if (wpm === 0) return 0; // Return 0 for 0 WPM as expected by tests
  return (wordCount / wpm) * 60;
};

export const updateReadingStats = (
  currentStats: ReadingStats,
  session: SessionResult,
  context?: {
    bookCompleted?: boolean;
    genre?: string;
    source?: string;
    format?: string;
    todayAvgWpm?: number;   // Phase 3: today's cumulative average WPM
    currentStreak?: number; // Phase 3: current reading streak from profile
    // Phase 4: time aggregates
    todaySeconds?: number;
    todayWords?: number;
    weekSeconds?: number;
    monthSeconds?: number;
  }
): ReadingStats => {
  // Handle null/undefined currentStats gracefully
  if (!currentStats) {
    currentStats = {
      wordsRead: 0,
      timeSpentSeconds: 0,
      lastWpm: 0,
      maxWpm: 0,
      avgWpm: 0,
      sessions: 0,
      history: [],
      booksCompleted: 0,
      genresRead: [],
      sourcesRead: [],
      formatsRead: [],
      timeOfDaySessions: { morning: 0, afternoon: 0, evening: 0, night: 0 },
      challengesCompleted: 0,
      perfectDaysCount: 0,
      peakDailyWords: 0,
      achievementsUnlocked: 0,
      // Phase 3 defaults:
      highWpmStreak: 0,
      streakMultiplier: 1,
      lastActiveDate: '',
      longestStreak: 0,
      newRecord: false,
      streakRecovery: false,
      streakBounceBack: false,
    };
  }
   
  // Handle null/undefined session gracefully
  if (!session) {
    session = {
      wordsRead: 0,
      durationSeconds: 0,
      avgWpm: 0,
      maxWpm: 0,
      date: new Date().toISOString(),
      paused: false,
      accuracy: 100,
    } as SessionResult;
  }

  // Calculate new global average (weighted by sessions is simple, weighted by words is better)
  const totalWords = currentStats.wordsRead + session.wordsRead;
  const totalTime = currentStats.timeSpentSeconds + session.durationSeconds;
   
  // robust average calculation: total words / (total minutes)
  const newGlobalAvgWpm = totalTime > 0 ? Math.round(totalWords / (totalTime / 60)) : session.avgWpm;

  // Update history - keep last 100 sessions to avoid unlimited localStorage growth
  const currentHistory = currentStats.history || [];
  const newHistory = [...currentHistory, session].slice(-100);

  // Carry over or initialize new fields
  const booksCompleted = currentStats.booksCompleted ?? 0;
  const genresRead = currentStats.genresRead ?? [];
  const sourcesRead = currentStats.sourcesRead ?? [];
  const formatsRead = currentStats.formatsRead ?? [];
  const timeOfDaySessions = currentStats.timeOfDaySessions ?? { morning: 0, afternoon: 0, evening: 0, night: 0 };
  const challengesCompleted = currentStats.challengesCompleted ?? 0;
  const perfectDaysCount = currentStats.perfectDaysCount ?? 0;
  const peakDailyWords = currentStats.peakDailyWords ?? 0;
  const achievementsUnlocked = currentStats.achievementsUnlocked ?? 0;

  // Apply context updates
  if (context?.bookCompleted) {
    // Increment only if not already counted? We'll trust caller to only set when newly completed
    // Use functional update to avoid double-count if multiple sessions in same tick
    // But we'll just increment; caller should ensure it's only set once per book
    // We'll increment by 1
    // To be safe, we could check if this session's book is already in sourcesRead? Not exactly.
    // We'll simply increment; the caller (App.tsx) will track which books have been counted.
    // For now, just increment
    // Actually, we'll let caller handle increment to avoid double-count. So context.bookCompleted is a flag to increment.
    // We'll increment by 1 if true.
    // But we need to avoid double-count if multiple sessions in same book. Caller should only pass true once when completion first occurs.
    // So we'll just do:
    // booksCompleted += 1;
    // That's fine.
    // We'll add below.
  }

  if (context?.genre && !genresRead.includes(context.genre)) {
    genresRead.push(context.genre);
  }
  if (context?.source && !sourcesRead.includes(context.source)) {
    sourcesRead.push(context.source);
  }
  if (context?.format && !formatsRead.includes(context.format)) {
    formatsRead.push(context.format);
  }

  // Time of day from session date
  const sessionHour = new Date(session.date).getHours();
  if (sessionHour >= 5 && sessionHour < 11) timeOfDaySessions.morning++;
  else if (sessionHour >= 11 && sessionHour < 17) timeOfDaySessions.afternoon++;
  else if (sessionHour >= 17 && sessionHour < 21) timeOfDaySessions.evening++;
  else timeOfDaySessions.night++;

  // ==================== Phase 3: Streak Multiplier & Advanced Streaks ====================
  const { todayAvgWpm, currentStreak } = context || {};

  // Session date as YYYY-MM-DD
  const sessionDate = new Date(session.date).toISOString().split('T')[0];

  // Previous values (with defaults)
  const prevHighWpmStreak = currentStats.highWpmStreak ?? 0;
  const prevLastActiveDate = currentStats.lastActiveDate ?? '';
  const prevLongestStreak = currentStats.longestStreak ?? 0;
  const prevRecovery = currentStats.streakRecovery ?? false;
  const prevBounceBack = currentStats.streakBounceBack ?? false;

  // Compute days gap between last active date and current session date
  let daysGap = 0;
  if (prevLastActiveDate) {
    const lastDate = new Date(prevLastActiveDate);
    const currDate = new Date(sessionDate);
    lastDate.setHours(0, 0, 0, 0);
    currDate.setHours(0, 0, 0, 0);
    const diffMs = currDate.getTime() - lastDate.getTime();
    daysGap = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  // Determine new highWpmStreak
  let newHighWpmStreak = prevHighWpmStreak;
  if (todayAvgWpm !== undefined) {
    const todayMeets = todayAvgWpm >= 400;
    if (sessionDate !== prevLastActiveDate) {
      // First session of a new day (or first ever)
      if (!prevLastActiveDate) {
        // First ever session
        newHighWpmStreak = todayMeets ? 1 : 0;
      } else if (daysGap > 1) {
        // Gap of more than one day: reset streak, then evaluate today
        newHighWpmStreak = todayMeets ? 1 : 0;
      } else if (daysGap === 1) {
        // Consecutive day: continue streak if today meets threshold
        newHighWpmStreak = todayMeets ? prevHighWpmStreak + 1 : 0;
      }
      // If daysGap === 0 (same day), we do not update highWpmStreak (keep as is)
    }
    // If sessionDate === prevLastActiveDate, skip updating highWpmStreak
  }

  // Compute streakMultiplier (capped at 10)
  const newStreakMultiplier = Math.min(10, 1 + Math.floor(newHighWpmStreak / 5));

  // Update lastActiveDate to current session date
  const newLastActiveDate = sessionDate;

  // Update longestStreak and newRecord using currentStreak from profile
  let newLongestStreak = prevLongestStreak;
  let isNewRecord = false;
  if (currentStreak !== undefined && currentStreak > prevLongestStreak) {
    newLongestStreak = currentStreak;
    isNewRecord = true;
  }

  // Set recovery and bounce-back flags (one-time, persist once triggered)
  const newRecovery = prevRecovery || (daysGap === 1);
  const newBounceBack = prevBounceBack || (daysGap >= 7 && daysGap !== 0);

   return {
     wordsRead: totalWords,
     timeSpentSeconds: totalTime,
     lastWpm: session.avgWpm,
     maxWpm: Math.max(currentStats.maxWpm || 0, session.maxWpm),
     avgWpm: newGlobalAvgWpm,
     sessions: currentStats.sessions + 1,
     history: newHistory,
     booksCompleted: booksCompleted + (context?.bookCompleted ? 1 : 0),
     genresRead,
     sourcesRead,
     formatsRead,
     timeOfDaySessions,
     challengesCompleted,
     perfectDaysCount,
     peakDailyWords,
     achievementsUnlocked,
     // Phase 3 fields:
     highWpmStreak: newHighWpmStreak,
     streakMultiplier: newStreakMultiplier,
     lastActiveDate: newLastActiveDate,
     longestStreak: newLongestStreak,
     newRecord: isNewRecord,
     streakRecovery: newRecovery,
     streakBounceBack: newBounceBack,
     // Phase 4 fields: time aggregates
     todaySeconds: context?.todaySeconds || 0,
     todayWords: context?.todayWords || 0,
     weekSeconds: context?.weekSeconds || 0,
     monthSeconds: context?.monthSeconds || 0,
   };
};

/**
 * Upsert daily reading stat for a session
 * Updates the aggregate for the session's date
 */
export const upsertDailyReadingStat = async (session: SessionResult): Promise<{ date: string; totalWords: number; totalSeconds: number }> => {
   const date = new Date(session.date).toISOString().split('T')[0];
   const existing = await getDailyStat(date);
   const newWords = (existing?.totalWords || 0) + session.wordsRead;
   const newSeconds = (existing?.totalSeconds || 0) + session.durationSeconds;

   await upsertDailyStat({
     date,
     totalWords: newWords,
     totalSeconds: newSeconds,
   });

   return { date, totalWords: newWords, totalSeconds: newSeconds };
 };

/**
 * Get time aggregates for a given date: weekly (rolling 7 days) and monthly (rolling 30 days)
 */
export const getTimeAggregates = async (date: string): Promise<{
  todaySeconds: number;
  todayWords: number;
  weekSeconds: number;
  monthSeconds: number;
}> => {
  const today = new Date(date);
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  const allStats = await getAllDailyStats();

  // Helper: check if a stat's date falls within the past N days from today (inclusive)
  const isWithinDays = (statDate: string, days: number): boolean => {
    const d = new Date(statDate);
    const diffMs = today.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays < days;
  };

  const todayStat = allStats.find((s) => s.date === todayStr);
  const todaySeconds = todayStat?.totalSeconds || 0;
  const todayWords = todayStat?.totalWords || 0;

  const weekStats = allStats.filter((s) => isWithinDays(s.date, 7));
  const monthStats = allStats.filter((s) => isWithinDays(s.date, 30));

  const weekSeconds = weekStats.reduce((sum: number, s) => sum + s.totalSeconds, 0);
  const monthSeconds = monthStats.reduce((sum: number, s) => sum + s.totalSeconds, 0);

  return { todaySeconds, todayWords, weekSeconds, monthSeconds };
};
